import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Profile {
  id: string;
  /** Production `profiles` schema stores the full name as a single column.
   *  Older migrations referenced first_name/last_name/phone_number but those
   *  columns aren't present in prod — keep this shape aligned with reality. */
  full_name?: string | null;
  username?: string | null;
  avatar_url?: string | null;
  trial_status?: string | null;
  signup_date?: string | null;
}

// Module-level flag to prevent infinite retry on profile fetch failure (survives component remounts)
let _profileFetchFailed = false;

/** Reset the profile fetch failure flag (call on sign-in / sign-out) */
export function resetProfileFetchFlag() {
  _profileFetchFailed = false;
}

export const useProfile = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const isMountedRef = useRef(true);

  // Own the mount lifecycle. Previously useAuth (the only caller) flipped
  // this ref externally — fine while useAuth was the sole consumer, but
  // any new caller of useProfile() would have isMountedRef stuck at true
  // forever and trigger setState-after-unmount warnings. The ref is
  // still exposed in the return so existing callers don't break.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchProfile = useCallback(async (userId: string) => {
    // Module-level guard: don't retry if we already failed (survives remounts)
    if (_profileFetchFailed) return;

    try {
      // SELECT only columns that actually exist in prod's `profiles` table.
      // The migrations directory describes first_name/last_name/phone_number
      // but the live schema collapsed these into `full_name` at some point
      // and the migrations were never reconciled — querying the legacy
      // columns 400s with PostgREST code 42703 ("column does not exist")
      // and locks every user out of their dashboard.
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, username, avatar_url, trial_status, signup_date")
        .eq("id", userId)
        .maybeSingle();

      if (!isMountedRef.current) return;

      if (error) {
        console.warn("Profile fetch error:", error.code, error.message);
        _profileFetchFailed = true;
        return;
      }

      if (data) {
        setProfile(data as Profile);
      }

      // Update last_seen_at in background — fire and forget
      supabase
        .from("profiles")
        .update({ last_seen_at: new Date().toISOString() } as Record<string, unknown>)
        .eq("id", userId)
        .then(() => {}, () => {});
    } catch (err) {
      console.error("Error fetching profile:", err);
      _profileFetchFailed = true;
    }
  }, []);

  const clearProfile = useCallback(() => {
    setProfile(null);
  }, []);

  const getFullName = useCallback(() => {
    if (profile) {
      return (profile.full_name || profile.username || "").trim() || null;
    }
    return null;
  }, [profile]);

  return {
    profile,
    fetchProfile,
    clearProfile,
    getFullName,
    isMountedRef,
  };
};
